import React, { useState, useEffect, useRef } from "react";
import "./App.css";
import MapComponent from "./MapComponent";

// Helper function to get CSRF token from cookies
const getCookie = (name) => {
  const value = `; ${document.cookie}`;
  const parts = value.split(`; ${name}=`);
  if (parts.length === 2) return parts.pop().split(";").shift();
};

// Encrypted credentials (Base64 encoded)
const getAutoLoginCredentials = () => {
  const encoded =
    "eyJlbWFpbCI6IndlYnNpdGVhcHBAaWFwd2ViYXBwcy5jb20iLCJwYXNzd29yZCI6ImFkbWluMTIzIiwidGVuYW50IjoiTGliZXJpYSJ9";
  try {
    return JSON.parse(atob(encoded));
  } catch (e) {
    console.error("Error decoding auto-login credentials.");
    return null;
  }
};

const App = () => {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [tenant, setTenant] = useState("");
  const [errorMessage, setErrorMessage] = useState("");
  const [isLoggedIn, setIsLoggedIn] = useState(
    sessionStorage.getItem("token") !== null,
  );
  const [showPassword, setShowPassword] = useState(false);
  const [username, setUsername] = useState(
    sessionStorage.getItem("username") || "",
  );
  const [tenantname, setTenantname] = useState(
    sessionStorage.getItem("tenantname") || "",
  );
  const [tenantId, setTenantId] = useState(
    sessionStorage.getItem("tenantId") || "",
  );
  const [userId, setUserId] = useState(sessionStorage.getItem("userId") || "");
  const [role, setRole] = useState(sessionStorage.getItem("role") || null);
  const [isLoggingOut, setIsLoggingOut] = useState(false);
  const [isAutoLoggingIn, setIsAutoLoggingIn] = useState(false);

  const [emailPlaceholder, setEmailPlaceholder] = useState("Email Address");
  const [passwordPlaceholder, setPasswordPlaceholder] = useState("Password");
  const [tenantPlaceholder, setTenantPlaceholder] = useState("Customer Name");

  const isLoggingOutRef = useRef(false);
  const hasAttemptedAutoLogin = useRef(false);

  // Check if coming from main app via URL parameter
  const checkMainAppRedirect = () => {
    const urlParams = new URLSearchParams(window.location.search);
    return urlParams.get("from_main_app") === "true";
  };

  // Secure auto-login function
  const performAutoLogin = async () => {
    if (hasAttemptedAutoLogin.current) return;
    hasAttemptedAutoLogin.current = true;

    setIsAutoLoggingIn(true);
    setErrorMessage("");

    const csrftoken = getCookie("csrftoken");
    const credentials = getAutoLoginCredentials();

    if (!credentials) {
      setErrorMessage(
        "Automatic authentication failed. Please login manually.",
      );
      setIsAutoLoggingIn(false);
      return;
    }

    try {
      // **LOGIN API CALL**
      const response = await fetch(
        `${process.env.REACT_APP_API_BASE_URL}/login/`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            "X-CSRFToken": csrftoken,
          },
          body: JSON.stringify({
            username: credentials.email,
            password: credentials.password,
            tenant: credentials.tenant,
          }),
          credentials: "include",
        },
      );

      // CRITICAL SECURITY STEP: Clear credentials from memory immediately after use
      credentials.email = null;
      credentials.password = null;
      credentials.tenant = null;

      if (response.ok) {
        const data = await response.json();

        // Store in sessionStorage for temporary session
        sessionStorage.setItem("token", data.token);
        sessionStorage.setItem("tenantname", data.tenantname);
        sessionStorage.setItem("username", data.username);
        sessionStorage.setItem("role", data.role);
        sessionStorage.setItem("tenant_id", data.tenant_id);
        sessionStorage.setItem("userId", data.user_id);
        sessionStorage.setItem("isGuestUser", "true"); // Flag to indicate auto-login session

        setTenantId(data.tenant_id);
        setUserId(data.user_id);
        setRole(data.role);
        setUsername(data.username);
        setTenantname(data.tenantname);
        setIsLoggedIn(true); // ** App automatically open**
        setErrorMessage("");
        setIsLoggingOut(false);

        // Clean up URL parameters after successful login
        window.history.replaceState(
          {},
          document.title,
          window.location.pathname,
        );
      } else {
        setErrorMessage("Auto-login failed. Please try manual login.");
      }
    } catch (error) {
      setErrorMessage(
        "Connection failed during auto-login. Please try again later.",
      );
    } finally {
      setIsAutoLoggingIn(false);
    }
  };

  // Check for auto-login on component mount
  useEffect(() => {
    const isFromMainApp = checkMainAppRedirect();
    const hasToken = sessionStorage.getItem("token");

    if (isFromMainApp && !hasToken && !hasAttemptedAutoLogin.current) {
      performAutoLogin();
    }
  }, []);

  // Function to check if the token is valid (runs periodically if needed)
  const checkTokenValidity = async () => {
    const token = sessionStorage.getItem("token");
    if (!token) return;

    try {
      const response = await fetch(
        `${process.env.REACT_APP_API_BASE_URL}/verifytoken/`,
        {
          method: "GET",
          headers: {
            Authorization: `Token ${token}`,
            "Content-Type": "application/json",
          },
        },
      );

      if (!response.ok) {
        handleSessionExpired();
      }
    } catch (error) {
      handleSessionExpired();
    }
  };

  useEffect(() => {
    if (isLoggedIn) {
      const handleBeforeUnload = (event) => {
        if (isLoggingOutRef.current) {
          return;
        }
        event.preventDefault();
        event.returnValue = "";
      };

      window.addEventListener("beforeunload", handleBeforeUnload);

      return () => {
        window.removeEventListener("beforeunload", handleBeforeUnload);
      };
    }
  }, [isLoggedIn]);

  // Inactivity timeout
  useEffect(() => {
    let inactivityTimeout;

    const handleActivity = () => {
      clearTimeout(inactivityTimeout);
      inactivityTimeout = setTimeout(
        () => {
          handleSessionExpired();
        },
        8 * 60 * 60 * 1000,
      );
    };

    if (isLoggedIn) {
      window.addEventListener("mousemove", handleActivity);
      window.addEventListener("mousedown", handleActivity);
      window.addEventListener("keypress", handleActivity);
      window.addEventListener("touchstart", handleActivity);
      window.addEventListener("scroll", handleActivity);
      handleActivity();
    }

    return () => {
      window.removeEventListener("mousemove", handleActivity);
      window.removeEventListener("mousedown", handleActivity);
      window.removeEventListener("keypress", handleActivity);
      window.removeEventListener("touchstart", handleActivity);
      window.removeEventListener("scroll", handleActivity);
      clearTimeout(inactivityTimeout);
    };
  }, [isLoggedIn]);

  const handleLogin = async () => {
    setErrorMessage("");
    const csrftoken = getCookie("csrftoken");

    let timeout1, timeout2;

    try {
      timeout1 = setTimeout(() => {
        setErrorMessage("Connecting to server... please wait");
      }, 2000);

      timeout2 = setTimeout(() => {
        setErrorMessage("Still trying to connect... please wait");
      }, 4000);

      const response = await fetch(
        `${process.env.REACT_APP_API_BASE_URL}/login/`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            "X-CSRFToken": csrftoken,
          },
          body: JSON.stringify({
            username: email,
            password: password,
            tenant: tenant,
          }),
          credentials: "include",
        },
      );

      clearTimeout(timeout1);
      clearTimeout(timeout2);

      if (response.ok) {
        const data = await response.json();
        sessionStorage.setItem("token", data.token);
        sessionStorage.setItem("tenantname", data.tenantname);
        sessionStorage.setItem("username", data.username);
        sessionStorage.setItem("role", data.role);
        sessionStorage.setItem("tenant_id", data.tenant_id);
        sessionStorage.setItem("userId", data.user_id);
        sessionStorage.setItem("isGuestUser", "false");

        setTenantId(data.tenant_id);
        setUserId(data.user_id);
        setRole(data.role);
        setUsername(data.username);
        setTenantname(data.tenantname);
        setIsLoggedIn(true);
        setErrorMessage("");
        setIsLoggingOut(false);
      } else if (response.status === 401) {
        setErrorMessage("Invalid credentials. Please try again.");
      } else {
        const errorData = await response.json();
        setErrorMessage(errorData.detail || "Login failed. Please try again.");
      }
    } catch (error) {
      setErrorMessage("Something went wrong. Please try again later.");
    } finally {
      clearTimeout(timeout1);
      clearTimeout(timeout2);
    }
  };

  const handleLogout = () => {
    isLoggingOutRef.current = true;
    setIsLoggedIn(false);
    setIsLoggingOut(true);

    const isGuestUser = sessionStorage.getItem("isGuestUser") === "true";

    sessionStorage.clear();

    if (isGuestUser) {
      // For guest users (auto-logged-in), redirect back to main app
      window.location.href = "https://www.aimlocate.org";
    } else {
      // For manually logged-in users, simply reload the login screen
      console.log("Successfully logged out.");
      window.location.reload();
    }
  };

  const togglePasswordVisibility = () => {
    setShowPassword((prevState) => !prevState);
  };

  const handleSessionExpired = () => {
    if (!isLoggingOut) {
      isLoggingOutRef.current = true;

      const isGuestUser = sessionStorage.getItem("isGuestUser") === "true";

      setErrorMessage("Session expired. Please login again.");
      setIsLoggedIn(false);
      sessionStorage.clear();

      // Redirect guest users back to main app to re-initiate SSO
      // if (isGuestUser) {
      //   window.location.href = 'https://www.aimlocate.org';
      // }
    }
  };

  // Loading screen while auto-login is in progress
  if (isAutoLoggingIn) {
    return (
      <div className="background">
        <div className="loading-container">
          <div className="spinner"></div>
          <p className="loading-text">Connecting to AimLocate...</p>
        </div>
      </div>
    );
  }

  if (isLoggedIn) {
    return (
      <MapComponent
        tenantId={tenantId}
        userId={userId}
        role={role}
        username={username}
        tenantname={tenantname}
        handleLogout={handleLogout}
        handleSessionExpired={handleSessionExpired}
        isGuestUser={sessionStorage.getItem("isGuestUser") === "true"}
      />
    );
  }

  return (
    <div className="background">
      <div className="left-section">
        <div className="login-container">
          <img src="/logo.png" alt="logo" className="logo" />
          <h1 className="title">Welcome Back</h1>

          {errorMessage && <p className="error-message">{errorMessage}</p>}

          <div className="input-container">
            <i className="fa fa-envelope input-icon"></i>
            <input
              type="email"
              className="input"
              placeholder={emailPlaceholder}
              value={email}
              onFocus={() => setEmailPlaceholder("")}
              onBlur={() => setEmailPlaceholder("Email Address")}
              onChange={(e) => setEmail(e.target.value)}
              style={{ color: "#999" }}
            />
          </div>

          <div className="input-container">
            <i className="fa fa-lock input-icon"></i>
            <input
              type={showPassword ? "text" : "password"}
              className="input"
              placeholder={passwordPlaceholder}
              value={password}
              onFocus={() => setPasswordPlaceholder("")}
              onBlur={() => setPasswordPlaceholder("Password")}
              onChange={(e) => setPassword(e.target.value)}
              style={{ color: "#999" }}
            />
            <i
              className={`fa ${showPassword ? "fa-eye" : "fa-eye-slash"} password-toggle`}
              onClick={togglePasswordVisibility}
            ></i>
          </div>

          <div className="input-container">
            <i className="fa fa-industry input-icon"></i>
            <input
              type="text"
              className="input"
              placeholder={tenantPlaceholder}
              value={tenant}
              onFocus={() => setTenantPlaceholder("")}
              onBlur={() => setTenantPlaceholder("Customer Name")}
              onChange={(e) => setTenant(e.target.value)}
              style={{ color: "#999" }}
            />
          </div>

          <button className="sign-in-btn" onClick={handleLogin}>
            Sign In
          </button>
        </div>
      </div>

      <div className="right-section">
        <div className="image-container">
          <img src="/business.png" alt="buildings" className="illustration" />
        </div>
        <div className="bottom-text">
          <h2>
            Location Intelligence for{" "}
            <span className="highlight">BUSINESS</span>
          </h2>
          <p>
            We live in a connected economy. The ability to see where a client is
            gives you the ability to make better decisions and strategic
            choices.
          </p>
        </div>
        <div className="image-container">
          <img src="/govt.png" alt="buildings" className="illustration" />
        </div>
        <div className="bottom-text">
          <h2>
            Location Intelligence for{" "}
            <span className="highlight">GOVERNMENT</span>
          </h2>
          <p>
            Governments' work starts with a location. Knowing where someone is
            allows you to address issues and plan for the future.
          </p>
        </div>
      </div>
    </div>
  );
};

export default App;
